import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { securityAPI } from '../../services/api';
import { toast } from 'react-hot-toast';
import DashboardLayout from '../../components/layout/DashboardLayout';
import useDocumentTitle from '../../hooks/useDocumentTitle';
import { useNavigate, Link } from 'react-router-dom';

interface SecurityIssue {
  id: number;
  url: string;
  issue_type: string;
  severity: string;
  matched_pattern: string;
  matched_content: string;
  status: string;
  detected_at: string;
}

interface ScanStatus {
  scan_id: number;
  status: string;
  progress: number;
  total_pages: number;
  scanned_pages: number;
  issues_found: number;
  started_at: string;
}

interface RecentScan {
  id: number;
  status: string;
  scan_type: string;
  issues_found: number;
  started_at: string;
  completed_at: string;
}

const SecuritySummaryPage: React.FC = () => {
  useDocumentTitle('Security Summary');
  const { currentAsset } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [scanning, setScanning] = useState(false);
  const [scanStatus, setScanStatus] = useState<ScanStatus | null>(null);
  const [issues, setIssues] = useState<SecurityIssue[]>([]);
  const [recentScans, setRecentScans] = useState<RecentScan[]>([]);
  const [stats, setStats] = useState({
    total: 0,
    open: 0,
    resolved: 0,
    critical: 0,
    high: 0,
    medium: 0,
    low: 0,
    securityScore: 100,
  });
  const [elapsedTime, setElapsedTime] = useState(0);
  const [notifPrefs, setNotifPrefs] = useState({ daily_enabled: false, weekly_enabled: false, monthly_enabled: false });
  const [savingPrefs, setSavingPrefs] = useState<null | 'daily_enabled' | 'weekly_enabled' | 'monthly_enabled'>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const scanStartTimeRef = useRef<Date | null>(null);

  useEffect(() => {
    if (currentAsset) {
      loadSecurityData();
    }
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [currentAsset]);

  const loadSecurityData = async () => {
    if (!currentAsset) return;

    setLoading(true);
    try {
      const response = await securityAPI.getOverview(currentAsset.id);
      if (response.data.status === 'success') {
        const data = response.data.data;
        setRecentScans(data.recent_scans || []);
        setStats({
          total: data.total_issues || 0,
          open: data.open_issues || 0,
          resolved: data.resolved_issues || 0,
          critical: data.critical_issues || 0,
          high: data.high_issues || 0,
          medium: data.medium_issues || 0,
          low: data.low_issues || 0,
          securityScore: data.security_score || 100,
        });

        // Check if there's an active scan
        const activeScan = data.recent_scans?.find((s: RecentScan) => s.status === 'running');
        if (activeScan) {
          setScanning(true);
          scanStartTimeRef.current = new Date(activeScan.started_at);
          startPolling(activeScan.id);
          startTimer();
        }
      }

      // Load recent issues separately
      const logsResponse = await securityAPI.getLogs(currentAsset.id, { days: 30 });
      if (logsResponse.data.status === 'success') {
        setIssues(logsResponse.data.data.logs?.slice(0, 10) || []);
      }

      // Load notification preferences
      try {
        const prefsResponse = await securityAPI.getNotificationPreferences(currentAsset.id);
        if (prefsResponse.data.status === 'success') {
          setNotifPrefs({
            daily_enabled: !!prefsResponse.data.data.daily_enabled,
            weekly_enabled: !!prefsResponse.data.data.weekly_enabled,
            monthly_enabled: !!prefsResponse.data.data.monthly_enabled,
          });
        }
      } catch { /* non-fatal */ }
    } catch (error: any) {
      if (error.response?.status !== 401) {
        console.error('Failed to load security data:', error);
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleNotifPref = async (key: 'daily_enabled' | 'weekly_enabled' | 'monthly_enabled') => {
    if (!currentAsset || savingPrefs) return;
    const next = { ...notifPrefs, [key]: !notifPrefs[key] };
    setNotifPrefs(next); // optimistic
    setSavingPrefs(key);
    try {
      await securityAPI.setNotificationPreferences(currentAsset.id, next);
      const labels = { daily_enabled: 'Daily', weekly_enabled: 'Weekly', monthly_enabled: 'Monthly' };
      toast.success(`${labels[key]} security email ${next[key] ? 'enabled' : 'disabled'}`);
    } catch (error) {
      setNotifPrefs(notifPrefs); // revert on failure
      toast.error('Failed to update notification preference');
    } finally {
      setSavingPrefs(null);
    }
  };

  const startQuickScan = async () => {
    if (!currentAsset) return;

    setScanning(true);
    setElapsedTime(0);
    scanStartTimeRef.current = new Date();
    startTimer();

    try {
      const response = await securityAPI.runVulnerabilityScan(currentAsset.id, {
        modules: ['ssl', 'headers', 'clickjacking', 'exposure']
      });

      if (response.data.status === 'success') {
        const results = response.data.data;
        const issuesFound = results.issues_found || results.issues?.length || 0;

        toast.success(`Quick scan completed! Found ${issuesFound} issues.`);
        setScanStatus({
          scan_id: results.scan_id || 0,
          status: 'completed',
          progress: 100,
          total_pages: 3,
          scanned_pages: 3,
          issues_found: issuesFound,
          started_at: scanStartTimeRef.current?.toISOString() || ''
        });
      }
    } catch (error: any) {
      // Check if it's a "scan already running" error
      if (error.response?.status === 409) {
        toast.error('A scan is already running for this asset');
        const data = error.response.data;
        if (data.scan_id) {
          startPolling(data.scan_id);
          return; // Don't stop scanning state
        }
      } else {
        toast.error(error.response?.data?.message || 'Scan failed');
      }
    } finally {
      stopTimer();
      setScanning(false);
      loadSecurityData();
    }
  };

  const startPolling = (scanId: number) => {
    pollIntervalRef.current = setInterval(async () => {
      try {
        const response = await securityAPI.getScanStatus(scanId);
        if (response.data.status === 'success') {
          const status = response.data.data;
          setScanStatus(status);

          if (status.status === 'completed' || status.status === 'failed') {
            stopPolling();
            stopTimer();
            setScanning(false);
            loadSecurityData();
            if (status.status === 'completed') {
              toast.success('Scan completed!');
            }
          }
        }
      } catch (error) {
        console.error('Error polling scan status:', error);
      }
    }, 3000);
  };

  const stopPolling = () => {
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
      pollIntervalRef.current = null;
    }
  };

  const startTimer = () => {
    timerRef.current = setInterval(() => {
      if (scanStartTimeRef.current) {
        setElapsedTime(Math.floor((Date.now() - scanStartTimeRef.current.getTime()) / 1000));
      }
    }, 1000);
  };

  const stopTimer = () => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const downloadReport = async () => {
    if (!currentAsset) return;

    try {
      toast.loading('Generating PDF report...');
      const response = await securityAPI.generatePdfReport(currentAsset.id);

      // Create blob from response data (PDF binary)
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `security-report-${currentAsset.id}-${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.dismiss();
      toast.success('PDF Report downloaded successfully');
    } catch (error: any) {
      toast.dismiss();
      console.error('Failed to generate report:', error);
      toast.error('Failed to generate PDF report');
    }
  };

  const getSeverityColor = (severity: string) => {
    switch (severity?.toLowerCase()) {
      case 'critical': return 'danger';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'secondary';
      default: return 'secondary';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': return 'danger';
      case 'investigating': return 'warning';
      case 'false_positive': return 'secondary';
      case 'resolved': return 'success';
      default: return 'secondary';
    }
  };

  const getScoreColor = (score: number) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    if (score >= 40) return 'orange';
    return 'danger';
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="container-fluid">
        {/* Header */}
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <h1 className="h3 mb-1">Security Summary</h1>
            <p className="text-muted mb-0">Monitor and manage security vulnerabilities</p>
          </div>
          <div className="d-flex gap-2">
            <button className="btn btn-outline-primary" onClick={loadSecurityData}>
              <i className="bi bi-arrow-clockwise me-2"></i>Refresh
            </button>
            <button className="btn btn-outline-success" onClick={downloadReport}>
              <i className="bi bi-download me-2"></i>Download Report
            </button>
            <Link to="/security/scanning" className="btn btn-primary">
              <i className="bi bi-shield-check me-2"></i>Full Scan
            </Link>
          </div>
        </div>

        {/* Scan Progress */}
        {scanning && (
          <div className="card border-0 shadow-sm mb-4 border-start border-primary border-4">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex align-items-center">
                  <div className="spinner-border spinner-border-sm text-primary me-3" role="status"></div>
                  <h6 className="mb-0">Security Scan in Progress</h6>
                </div>
                <span className="badge bg-primary fs-6">
                  <i className="bi bi-clock me-1"></i>{formatTime(elapsedTime)}
                </span>
              </div>
              <div className="progress mb-2" style={{ height: '8px' }}>
                <div
                  className="progress-bar bg-primary progress-bar-striped progress-bar-animated"
                  style={{ width: `${scanStatus?.progress || 50}%` }}
                ></div>
              </div>
              <div className="d-flex justify-content-between text-muted small">
                <span>{scanStatus?.scanned_pages || 0} / {scanStatus?.total_pages || 3} modules scanned</span>
                <span>{scanStatus?.issues_found || 0} issues found</span>
              </div>
            </div>
          </div>
        )}

        {/* Stats Cards */}
        <div className="row mb-4 g-3">
          <div className="col-md-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <div className="bg-danger bg-opacity-10 rounded p-3">
                      <i className="bi bi-exclamation-triangle text-danger fs-4"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <h6 className="text-muted mb-1">Open Issues</h6>
                    <h3 className="mb-0 text-danger">{stats.open}</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <div className="bg-success bg-opacity-10 rounded p-3">
                      <i className="bi bi-check-circle text-success fs-4"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <h6 className="text-muted mb-1">Resolved</h6>
                    <h3 className="mb-0 text-success">{stats.resolved}</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <div className="bg-warning bg-opacity-10 rounded p-3">
                      <i className="bi bi-shield-exclamation text-warning fs-4"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <h6 className="text-muted mb-1">High/Critical</h6>
                    <h3 className="mb-0 text-warning">{stats.critical + stats.high}</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body">
                <div className="d-flex align-items-center">
                  <div className="flex-shrink-0">
                    <div className="bg-primary bg-opacity-10 rounded p-3">
                      <i className="bi bi-list-check text-primary fs-4"></i>
                    </div>
                  </div>
                  <div className="flex-grow-1 ms-3">
                    <h6 className="text-muted mb-1">Total Issues</h6>
                    <h3 className="mb-0">{stats.total}</h3>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Severity Breakdown & Security Score */}
        <div className="row mb-4">
          <div className="col-md-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white border-bottom py-3">
                <h5 className="mb-0">Issues by Severity</h5>
              </div>
              <div className="card-body">
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-1">
                    <span className="text-danger">Critical</span>
                    <span className="fw-semibold">{stats.critical}</span>
                  </div>
                  <div className="progress" style={{ height: '8px' }}>
                    <div className="progress-bar bg-danger" style={{ width: `${(stats.critical / Math.max(stats.total, 1)) * 100}%` }}></div>
                  </div>
                </div>
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-1">
                    <span className="text-warning">High</span>
                    <span className="fw-semibold">{stats.high}</span>
                  </div>
                  <div className="progress" style={{ height: '8px' }}>
                    <div className="progress-bar bg-warning" style={{ width: `${(stats.high / Math.max(stats.total, 1)) * 100}%` }}></div>
                  </div>
                </div>
                <div className="mb-3">
                  <div className="d-flex justify-content-between mb-1">
                    <span className="text-info">Medium</span>
                    <span className="fw-semibold">{stats.medium}</span>
                  </div>
                  <div className="progress" style={{ height: '8px' }}>
                    <div className="progress-bar bg-info" style={{ width: `${(stats.medium / Math.max(stats.total, 1)) * 100}%` }}></div>
                  </div>
                </div>
                <div>
                  <div className="d-flex justify-content-between mb-1">
                    <span className="text-secondary">Low</span>
                    <span className="fw-semibold">{stats.low}</span>
                  </div>
                  <div className="progress" style={{ height: '8px' }}>
                    <div className="progress-bar bg-secondary" style={{ width: `${(stats.low / Math.max(stats.total, 1)) * 100}%` }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="col-md-6">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Security Score</h5>
                <button
                  className="btn btn-sm btn-outline-primary"
                  onClick={startQuickScan}
                  disabled={scanning}
                >
                  <i className="bi bi-lightning me-1"></i>Quick Scan
                </button>
              </div>
              <div className="card-body d-flex flex-column align-items-center justify-content-center">
                <div className="position-relative mb-3">
                  <svg width="150" height="150" viewBox="0 0 150 150">
                    <circle cx="75" cy="75" r="65" fill="none" stroke="#e9ecef" strokeWidth="12" />
                    <circle
                      cx="75"
                      cy="75"
                      r="65"
                      fill="none"
                      stroke={stats.securityScore >= 80 ? '#198754' : stats.securityScore >= 60 ? '#ffc107' : '#dc3545'}
                      strokeWidth="12"
                      strokeLinecap="round"
                      strokeDasharray={`${(stats.securityScore / 100) * 408} 408`}
                      transform="rotate(-90 75 75)"
                    />
                  </svg>
                  <div className="position-absolute top-50 start-50 translate-middle text-center">
                    <div className={`h2 mb-0 text-${getScoreColor(stats.securityScore)}`}>{Math.round(stats.securityScore)}</div>
                    <div className="text-muted small">Score</div>
                  </div>
                </div>
                <p className="text-muted text-center mb-0">
                  {stats.open === 0 ? 'All clear! No open security issues.' : `${stats.open} issues need attention`}
                </p>
                <Link to="/security/results" className="btn btn-outline-primary btn-sm mt-3">
                  <i className="bi bi-file-earmark-text me-1"></i>View Full Report
                </Link>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Scans & Issues */}
        <div className="row">
          <div className="col-lg-6">
            <div className="card border-0 shadow-sm mb-4">
              <div className="card-header bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Recent Scans</h5>
                <Link to="/security/scanning" className="btn btn-sm btn-outline-primary">View All</Link>
              </div>
              <div className="card-body p-0">
                <div className="table-responsive">
                  <table className="table table-hover mb-0">
                    <thead className="table-light">
                      <tr>
                        <th>ID</th>
                        <th>Type</th>
                        <th>Status</th>
                        <th>Issues</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentScans.slice(0, 5).map((scan) => (
                        <tr key={scan.id}>
                          <td className="fw-medium">#{scan.id}</td>
                          <td><span className="badge bg-secondary text-capitalize">{scan.scan_type}</span></td>
                          <td>
                            <span className={`badge bg-${scan.status === 'completed' ? 'success' : scan.status === 'running' ? 'primary' : 'danger'}`}>
                              {scan.status}
                            </span>
                          </td>
                          <td>
                            {scan.issues_found > 0 ? (
                              <span className="badge bg-warning text-dark">{scan.issues_found}</span>
                            ) : (
                              <span className="text-success">0</span>
                            )}
                          </td>
                          <td className="small text-muted">{new Date(scan.started_at).toLocaleDateString()}</td>
                        </tr>
                      ))}
                      {recentScans.length === 0 && (
                        <tr>
                          <td colSpan={5} className="text-center text-muted py-4">
                            No scans yet
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div className="col-lg-6">
            <div className="card border-0 shadow-sm">
              <div className="card-header bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
                <h5 className="mb-0">Recent Issues</h5>
                <Link to="/security/vulnerabilities" className="btn btn-sm btn-outline-primary">View All</Link>
              </div>
              <div className="card-body p-0">
                <div className="list-group list-group-flush">
                  {issues.slice(0, 5).map((issue) => (
                    <div key={issue.id} className="list-group-item">
                      <div className="d-flex align-items-center">
                        <span className={`badge bg-${getSeverityColor(issue.severity)} me-2`}>
                          {issue.severity?.toUpperCase()}
                        </span>
                        <div className="flex-grow-1">
                          <div className="fw-medium small">{issue.issue_type.replace(/_/g, ' ')}</div>
                          <small className="text-muted">{issue.url}</small>
                        </div>
                        <span className={`badge bg-${getStatusColor(issue.status)}`}>
                          {issue.status}
                        </span>
                      </div>
                    </div>
                  ))}
                  {issues.length === 0 && (
                    <div className="list-group-item text-center text-muted py-4">
                      <i className="bi bi-shield-check fs-2 d-block mb-2 text-success"></i>
                      No security issues detected
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Automated Email Notifications */}
        <div className="row mt-4">
          <div className="col-12">
            <div className="card">
              <div className="card-header d-flex align-items-center">
                <i className="bi bi-bell me-2"></i>
                <h5 className="mb-0">Automated Security Emails</h5>
              </div>
              <div className="card-body">
                <p className="text-muted mb-4">
                  Choose how often you want a security summary emailed to your team. Pick the cadence that suits you &mdash;
                  daily can be a lot, so weekly or monthly is a lighter option. All are off by default.
                </p>
                <div className="row g-3">
                  {([
                    { key: 'daily_enabled', title: 'Daily', desc: 'A summary every morning', icon: 'bi-calendar-day' },
                    { key: 'weekly_enabled', title: 'Weekly', desc: 'A recap every Monday', icon: 'bi-calendar-week' },
                    { key: 'monthly_enabled', title: 'Monthly', desc: 'An overview on the 1st', icon: 'bi-calendar-month' },
                  ] as const).map((opt) => (
                    <div className="col-md-4" key={opt.key}>
                      <div
                        className={`border rounded p-3 h-100 d-flex align-items-start justify-content-between ${notifPrefs[opt.key] ? 'border-primary bg-light' : ''}`}
                      >
                        <div>
                          <div className="fw-semibold mb-1">
                            <i className={`bi ${opt.icon} me-2`}></i>{opt.title}
                          </div>
                          <div className="small text-muted">{opt.desc}</div>
                        </div>
                        <div className="form-check form-switch ms-2">
                          <input
                            className="form-check-input"
                            type="checkbox"
                            role="switch"
                            checked={notifPrefs[opt.key]}
                            disabled={savingPrefs === opt.key}
                            onChange={() => toggleNotifPref(opt.key)}
                            style={{ cursor: 'pointer' }}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default SecuritySummaryPage;
